// src/components/Navbar/Navbar.jsx
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo.png";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: "Our Projects", path: "/projects" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact Us", path: "/contact" },
  ];

  return (
    <>
      {/* NAVBAR */}
      <div className="fixed top-0 left-0 w-full h-16 md:h-20 z-50 bg-[#d2e6d0]">
        {/* SVG */}
        <svg
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          className="absolute top-0 left-0 w-full h-full z-0"
        >
          <path
            d="M0,0 L0,60 C300,120 1140,0 1440,60 L1440,0 Z"
            fill="#ffffff"
          />
        </svg>

        <div className="relative z-10 h-full flex items-center justify-between max-w-[1440px] mx-auto px-4 md:px-6 lg:px-12">
          {/* LOGO */}
          <Link
            to="/"
            className="flex items-center gap-2 md:gap-4 group transition-transform duration-300 hover:scale-105"
          >
            <img
              src={logo}
              alt="Logo"
              className="w-9 h-9 md:w-12 md:h-12 object-contain"
            />
            <div className="leading-tight">
              <h2 className="text-sm md:text-lg font-bold bg-gradient-to-r from-green-900 to-green-300 bg-clip-text text-transparent">
                CHELINKO
              </h2>
              <p className="text-[9px] sm:text-[10px] md:text-sm text-green-800/80 uppercase tracking-wide leading-tight">
                Stainless Steel & Metal Fabrication
              </p>
            </div>
          </Link>

          {/* DESKTOP NAV (unchanged) */}
          <div className="hidden md:flex items-center gap-6 relative h-full">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className="relative flex items-center h-full px-4"
                >
                  <motion.span
                    className={`text-sm sm:text-base font-medium transition-colors ${
                      isActive ? "text-green-900" : "text-gray-800"
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    {item.name}
                  </motion.span>

                  {isActive && (
                    <motion.div
                      layoutId="underline"
                      className="absolute bottom-2 left-0 right-0 mx-auto h-[2px] w-[60%] bg-green-700 rounded-full"
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 30,
                      }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* MOBILE BUTTON */}
          <button
            className="md:hidden text-green-900"
            onClick={() => setOpen(true)}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-16 md:h-20" />

      {/* MOBILE MENU */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 z-30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />

            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-16 right-0 h-[calc(100%-4rem)] w-[75%] max-w-xs bg-white z-40 shadow-lg"
            >
              <div className="flex justify-between items-center px-4 py-3 border-b">
                <h3 className="text-sm font-semibold text-green-900">Menu</h3>
                <button onClick={() => setOpen(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="flex flex-col p-4 gap-2">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;

                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setOpen(false)}
                      className={`px-3 py-2 text-sm rounded-md transition ${
                        isActive
                          ? "bg-green-50 text-green-800"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
